// Expenses service - moved from database.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';

// Import related services
import { journalEntriesService } from './journal-entries';

export const expensesService = {
  async getExpenses(companyId) {
    try {
      const expensesRef = collection(db, 'expenses');
      
      // Try ordered query first
      try {
        const q = query(expensesRef, where('companyId', '==', companyId), orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (orderError) {
        console.log('Ordered query failed, trying with createdAt:', orderError.message);
        
        // Try with createdAt if date orderBy fails
        try {
          const q = query(expensesRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
          const snapshot = await getDocs(q);
          return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (createdAtError) {
          console.log('CreatedAt query failed, falling back to simple query:', createdAtError.message);
          throw createdAtError; // Fall through to catch block
        }
      }
    } catch (error) {
      console.error('Error with ordered queries, falling back to simple query:', error);
      
      // Fallback to simple query without orderBy if indexes don't exist
      const expensesRef = collection(db, 'expenses');
      const q = query(expensesRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort in JavaScript as fallback - prioritize actual expense dates
      return expenses.sort((a, b) => {
        // Get expense date (prioritize date, then expenseDate, then createdAt)
        const getExpenseDate = (expense) => {
          if (expense.date) {
            return expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
          }
          if (expense.expenseDate) {
            return expense.expenseDate?.toDate ? expense.expenseDate.toDate() : new Date(expense.expenseDate);
          }
          if (expense.createdAt) {
            return expense.createdAt?.toDate ? expense.createdAt.toDate() : new Date(expense.createdAt);
          }
          return new Date(0); // Very old date as fallback
        };
        
        const dateA = getExpenseDate(a);
        const dateB = getExpenseDate(b);
        
        // Primary sort by expense date (newest first)
        const dateDiff = dateB - dateA;
        
        // Secondary sort by creation time if dates are the same
        if (dateDiff === 0) {
          const createdA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const createdB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return createdB - createdA;
        }
        
        return dateDiff;
      });
    }
  },
  async getExpense(expenseId) {
    const expenseRef = doc(db, 'expenses', expenseId);
    const snapshot = await getDoc(expenseRef);
    if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
    return null;
  },
  async addExpense(expenseData, skipJournalEntry = false) {
    // ✅ PREVENT DOUBLE-ENTRY: Validate that manual expense doesn't conflict with automatic journal entries
    if (!skipJournalEntry && !expenseData.autoGenerated) {
      try {
        // TODO: Implement validation when needed
        // await journalEntriesService.validateManualEntryAllowed(expenseData.companyId, expenseData);
      } catch (validationError) {
        throw new Error(`Manual expense creation blocked: ${validationError.message}`);
      }
    }

    // ✅ VALIDATE VAT CALCULATION TO PREVENT DOUBLE COUNTING
    if (expenseData.baseAmount !== undefined && expenseData.vatAmount !== undefined && expenseData.totalAmount !== undefined) {
      const calculatedTotal = (expenseData.baseAmount || 0) + (expenseData.vatAmount || 0);
      if (Math.abs(calculatedTotal - expenseData.totalAmount) > 0.01) {
        throw new Error(`VAT calculation validation failed: baseAmount (${expenseData.baseAmount}) + vatAmount (${expenseData.vatAmount}) != totalAmount (${expenseData.totalAmount})`);
      }
      
      // Mark as VAT calculation valid
      expenseData.vatCalculationValid = true;
    }

    // Filter out undefined values to prevent Firestore errors
    const cleanExpenseData = Object.fromEntries(
      Object.entries(expenseData).filter(([key, value]) => value !== undefined)
    );

    const expensesRef = collection(db, 'expenses');
    const docRef = await addDoc(expensesRef, { 
      ...cleanExpenseData, 
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Automatically create journal entry for the expense (unless skipped)
    if (!skipJournalEntry) {
      try {
        await journalEntriesService.createExpenseJournalEntry(
          expenseData.companyId,
          { ...expenseData, id: docRef.id },
          expenseData.createdBy
        );
      } catch (error) {
        console.error('Error creating automatic journal entry for expense:', error);
        // Don't fail the expense creation if journal entry fails
      }
    }
    
    return docRef.id;
  },
  async updateExpense(expenseId, expenseData) {
    // ✅ VALIDATE VAT CALCULATION TO PREVENT DOUBLE COUNTING
    if (expenseData.baseAmount !== undefined && expenseData.vatAmount !== undefined && expenseData.totalAmount !== undefined) {
      const calculatedTotal = (expenseData.baseAmount || 0) + (expenseData.vatAmount || 0);
      if (Math.abs(calculatedTotal - expenseData.totalAmount) > 0.01) {
        throw new Error(`VAT calculation validation failed: baseAmount (${expenseData.baseAmount}) + vatAmount (${expenseData.vatAmount}) != totalAmount (${expenseData.totalAmount})`);
      }
      
      // Mark as VAT calculation valid
      expenseData.vatCalculationValid = true;
    }

    // Filter out undefined values to prevent Firestore errors
    const cleanExpenseData = Object.fromEntries(
      Object.entries(expenseData).filter(([key, value]) => value !== undefined)
    );
    
    const expenseRef = doc(db, 'expenses', expenseId);
    await updateDoc(expenseRef, { ...cleanExpenseData, updatedAt: serverTimestamp() });
    return true;
  },
  async deleteExpense(expenseId) {
    const expenseRef = doc(db, 'expenses', expenseId);
    await deleteDoc(expenseRef);
    return true;
  }
}; 